import { isToolUIPart, type DynamicToolUIPart, type ToolUIPart, type UIMessage } from 'ai';

type InflightToolPart = (ToolUIPart | DynamicToolUIPart) & { state: 'input-streaming' | 'input-available' };

/**
 * RTDB から復元したメッセージの実行途中の tool part を中断扱い（output-error）に正規化する。
 *
 * ストリーム切断（ネットワーク断・停止ボタン・サーバータイムアウト等）が起きると、
 * tool part が input-streaming / input-available のまま永続化されることがある。
 * ツール実行はリロード後に再開されないため、そのまま復元すると
 * 「実行中...」インジケーターが永久に表示され続けてしまう。
 */
export function normalizeRestoredMessage(message: UIMessage, interruptedText: string): UIMessage {
  if (!message.parts?.some(isInflightToolPart)) {
    return message;
  }
  return {
    ...message,
    parts: message.parts.map((part) => (isInflightToolPart(part) ? toInterruptedPart(part, interruptedText) : part)),
  };
}

/**
 * メッセージ群に含まれる実行途中の tool part を中断扱い（output-error）に確定させる。
 *
 * ストリームの異常終了（タイムアウト・ネットワーク断）や停止ボタンでの中断直後に呼び、
 * 「実行中...」インジケーターが表示され続けるのを防ぐ。
 * 対象の part がひとつもなければ null を返す（呼び出し側は再描画・保存をスキップできる）。
 */
export function normalizeInterruptedMessages(messages: UIMessage[], interruptedText: string): UIMessage[] | null {
  let changed = false;
  const normalized = messages.map((message) => {
    const next = normalizeRestoredMessage(message, interruptedText);
    if (next !== message) {
      changed = true;
    }
    return next;
  });
  return changed ? normalized : null;
}

function isInflightToolPart(part: UIMessage['parts'][number]): part is InflightToolPart {
  return isToolUIPart(part) && (part.state === 'input-streaming' || part.state === 'input-available');
}

// SDK 側で tool part にフィールドが追加されても情報を落とさないよう、spread で既存フィールドを
// 保持しつつ state と errorText だけ上書きする
function toInterruptedPart(part: InflightToolPart, interruptedText: string): ToolUIPart | DynamicToolUIPart {
  return {
    ...part,
    input: part.input,
    state: 'output-error',
    errorText: interruptedText,
  };
}
